import * as XLSX from 'xlsx';
import type { FormValues } from '../../promotions/types';

type Row = Record<string, string | number>;
type XlsxExporter = (values: FormValues) => void;

interface XlsxExportPayload {
  cols: string[];
  headers: Row;
  rows: Row[];
  fileName: string;
}

let activeXlsxExportCollector: { payload?: XlsxExportPayload } | null = null;

const CHILD_COUNT_KEY = 'qtdCardsFilhos';
const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;
const CHILD_MAX_COUNT = 4;
const CHILD_EXPORT_TEMPLATE_IDS = new Set(['t3', 't4a', 't4b']);

// --- helpers ---

function str(v: string | string[] | undefined): string {
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return v.join(',');
  return String(v);
}

function num(v: string | string[] | undefined): number | string {
  const s = str(v);
  if (s === '') return '';
  const n = Number(s.replace(',', '.'));
  return isNaN(n) ? s : n;
}

// Strips the leading "N — " label prefix to get the raw numeric value
function raw(v: string | string[] | undefined): string {
  const s = str(v);
  const match = s.match(/^(\d+)/);
  return match ? match[1] : s;
}

function rawMulti(v: string | string[] | undefined): string {
  if (!v) return '';
  const arr = Array.isArray(v) ? v : [v as string];
  return arr.map((x) => {
    const match = String(x).match(/^(\d+)/);
    return match ? match[1] : x;
  }).join(',');
}

function normalizeChildCount(value: unknown): number {
  if (Array.isArray(value)) return 0;
  const parsed = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(CHILD_MAX_COUNT, parsed));
}

function getChildFieldInfo(key: string): { baseKey: string; childNumber: number } | null {
  const match = key.match(CHILD_FIELD_SUFFIX_RE);
  if (!match) return null;

  const childNumber = parseInt(match[1], 10);
  if (Number.isNaN(childNumber)) return null;

  return {
    baseKey: key.replace(CHILD_FIELD_SUFFIX_RE, ''),
    childNumber,
  };
}

function removeChildOnlyKeys(values: FormValues): FormValues {
  const next: FormValues = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key === CHILD_COUNT_KEY || getChildFieldInfo(key)) return;
    next[key] = value;
  });

  return next;
}

function buildChildValues(values: FormValues, childNumber: number): FormValues {
  const childValues: FormValues = {
    ...removeChildOnlyKeys(values),
    numeroCard: String(childNumber),
  };

  Object.entries(values).forEach(([key, value]) => {
    const childInfo = getChildFieldInfo(key);
    if (!childInfo || childInfo.childNumber !== childNumber) return;
    childValues[childInfo.baseKey] = value;
  });

  childValues.numeroCard = String(childNumber);
  return childValues;
}

function getParentAndChildValues(templateId: string, values: FormValues): FormValues[] {
  const parentValues = removeChildOnlyKeys(values);

  if (!CHILD_EXPORT_TEMPLATE_IDS.has(templateId) || parentValues.numeroCard !== '1') {
    return [parentValues];
  }

  const childCount = normalizeChildCount(values[CHILD_COUNT_KEY]);
  if (childCount <= 0) return [parentValues];

  const result = [parentValues];

  for (let childNumber = 2; childNumber <= childCount + 1; childNumber += 1) {
    result.push(buildChildValues(values, childNumber));
  }

  return result;
}

function isMeaningfulRow(row: Row): boolean {
  return Object.values(row).some((value) => String(value ?? '').trim() !== '');
}

// --- Row header comment rows (2 rows per CSV spec) ---

const REBAIXA_SIMPLES_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Nome da promo / cupom': 'Nome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Tipo de desconto promocional': '1 = By_percent: valor percentual de desconto do item a ser fornecida\n2 = Cart_fixed: valor bruto (R$) de desconto no valor total do carrinho a ser fornecido\n3 = By_fixed: valor bruto (R$) de desconto do item a ser fornecido',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual ou monetário no item, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%" e "R$"',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Critério de desconto': '1 = Quantidade min/máx\n2 = Valor min/máx\nPreencha os campos a seguir de acordo com a sua escolha',
  'Quantidade mínima': 'Quantidade mínima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Quantidade máxima': 'Quantidade máxima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1 e se qtd máx não for ilimitada\nSe qtd máx for ilimitada = não preencher (deixar o campo vazio)',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção\nPreencher apenas se Critério de desconto = 2',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção\nPreencher apenas se Critério de desconto = 2 e se valor máx não for ilimitado\nSe valor máx for ilimitado = não preencher (deixar o campo vazio)',
  'Tipo de item a ser INCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem INCLUÍDOS na promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'EANs obrigatórios': 'Preencher os EANs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKUs obrigatórios': 'Preencher os SKUs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKU é combo?': '0 = Não\n1 = Sim\nPreencher caso Itens a serem incluídos na promoção = 2',
  'Indústria da família': 'Apenas uma indústria por família',
};

const REBAIXA_PROGRESSIVA_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Nome da promo / cupom': 'Nome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Número do card na promo': 'Valor numérico sequencial de acordo com a quantidade de cards na promo\n0 = card único,\n1 = card principal / pai,\n2 em diante = cards secundários / dependentes / filhos',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%"',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Critério de desconto': '1 = Quantidade min/máx\n2 = Valor min/máx\nPreencha os campos a seguir de acordo com a sua escolha',
  'Quantidade mínima': 'Quantidade mínima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Quantidade máxima': 'Quantidade máxima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Tipo de item a ser INCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem INCLUÍDOS na promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'EANs obrigatórios': 'Preencher os EANs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKUs obrigatórios': 'Preencher os SKUs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKU é combo?': '0 = Não\n1 = Sim\nPreencher caso Itens a serem incluídos na promoção = 2',
  'Indústria da família': 'Apenas uma indústria por família',
};

const COMPRE_GANHE_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Nome da promo': 'Nome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Número do card na promo': 'Valor numérico sequencial de acordo com a quantidade de cards na promo\n0 = card único,\n1 = card principal / pai,\n2 em diante = cards secundários / dependentes / filhos',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual ou monetário, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%" e "R$"\nVazio para família tipo 1 (requisito)\nObrigatório para famílias tipo 2 e 3 (benefício e ambos)',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção',
  'Tipo de família': '1 = Requisito / obrigatória\n2 = Benefício\n3 = Ambos',
  'Nome da família': 'Sugestão de nome a ser utilizado na família',
  'Indústria da família': 'Apenas uma indústria por família',
  'OBS Família existente': 'Caso queira reaproveitar uma família existente, cite aqui o nome da família ou da promoção na qual ela foi utilizada',
  'SKUs da família': 'preencher no campo itens a serem incluídos',
  'Quantidade de EANs distintos para ativação da promo': 'Quantidade de EANs diferentes para ativar a promoção',
  'Quantidade mínima para ativar a promo': 'Quantidade mínima de EANs para ativar a promo',
  'Quantidade máxima para ativar a promo': 'Quantidade máxima de EANs para ativar a promo',
  'Quantidade de famílias para ativar a promo': 'Esse número deve ser:\n- maior ou igual ao total de famílias obrigatórias\n- menor ou igual ao total de famílias na promo',
  'Família obrigatória para ativação?': 'vazio = não\n1 = sim',
};

// --- date format: form stores "dd/mm/yyyy hh:mm", CSV needs "dd/mm/yyyy hh:mm:ss" ---
function fmtDate(v: string | string[] | undefined): string {
  const s = str(v);
  if (!s) return '';
  // If already has seconds, return as-is
  if (/\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  // Append :00 for seconds
  if (/\d{2}:\d{2}$/.test(s)) return s + ':00';
  return s;
}

// Build sellers string — values from multiselect are stored as option values
// The CSV example uses seller names separated by comma; we use the raw values
function fmtSellers(v: string | string[] | undefined): string {
  return str(v);
}

export function exportRebaixaSimples(values: FormValues): void {
  const cols = Object.keys(REBAIXA_SIMPLES_HEADERS);

  const dataRow: Row = {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '1',
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Tipo de desconto promocional': raw(values['tipoDesconto']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Critério de desconto': raw(values['criterioDesconto']),
    'Quantidade mínima': num(values['qtdMinima']),
    'Quantidade máxima': num(values['qtdMaxima']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Tipo de item a ser INCLUÍDO na promoção': raw(values['tipoItemIncluido']),
    'Itens a serem INCLUÍDOS na promoção': str(values['itensIncluidos']),
    'EANs obrigatórios': str(values['eansObrigatorios']),
    'SKUs obrigatórios': str(values['skusObrigatorios']),
    'SKU é combo?': raw(values['skuCombo']),
    'Indústria da família': str(values['industriaFamilia']),
  };

  // 4 empty trailing rows with just industriaFamilia (matching the CSV pattern)
  const emptyRow: Row = Object.fromEntries(cols.map((c) => [c, '']));
  const trailingRows = [1, 2, 3].map(() => ({
    ...emptyRow,
    'Indústria da família': str(values['industriaFamilia']),
  }));

  buildAndDownload(
    cols,
    REBAIXA_SIMPLES_HEADERS,
    [dataRow, ...trailingRows],
    'Template_Rebaixa_Simples.xlsx',
  );
}

export function exportRebaixaProgressiva(values: FormValues): void {
  const cols = Object.keys(REBAIXA_PROGRESSIVA_HEADERS);

  const dataRow: Row = {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '2',
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Número do card na promo': num(values['numeroCard']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Critério de desconto': raw(values['criterioDesconto']),
    'Quantidade mínima': num(values['qtdMinima']),
    'Quantidade máxima': num(values['qtdMaxima']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Tipo de item a ser INCLUÍDO na promoção': raw(values['tipoItemIncluido']),
    'Itens a serem INCLUÍDOS na promoção': str(values['itensIncluidos']),
    'EANs obrigatórios': str(values['eansObrigatorios']),
    'SKUs obrigatórios': str(values['skusObrigatorios']),
    'SKU é combo?': raw(values['skuCombo']),
    'Indústria da família': str(values['industriaFamilia']),
  };

  const emptyRow: Row = Object.fromEntries(cols.map((c) => [c, '']));
  const trailingRows = [1].map(() => ({
    ...emptyRow,
    'Indústria da família': str(values['industriaFamilia']),
  }));

  buildAndDownload(
    cols,
    REBAIXA_PROGRESSIVA_HEADERS,
    [dataRow, ...trailingRows],
    'Template_Rebaixa_Progressiva.xlsx',
  );
}

function buildCompreEGanheRow(values: FormValues): Row {
  return {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '3',
    'Nome da promo': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Número do card na promo': num(values['numeroCard']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Tipo de família': raw(values['tipoFamilia']),
    'Nome da família': str(values['nomeFamilia']),
    'Indústria da família': str(values['industriaFamilia']),
    'OBS Família existente': str(values['obsFamilia']),
    'SKUs da família': str(values['skusFamilia']),
    'Quantidade de EANs distintos para ativação da promo': num(values['qtdEANsDistintos']),
    'Quantidade mínima para ativar a promo': num(values['qtdMinimaAtivacao']),
    'Quantidade máxima para ativar a promo': num(values['qtdMaximaAtivacao']),
    'Quantidade de famílias para ativar a promo': num(values['qtdFamiliasAtivacao']),
    'Família obrigatória para ativação?': raw(values['familiaObrigatoria']),
  };
}

export function exportCompreEGanhe(values: FormValues): void {
  const cols = Object.keys(COMPRE_GANHE_HEADERS);
  const rows = getParentAndChildValues('t3', values).map(buildCompreEGanheRow);

  buildAndDownload(
    cols,
    COMPRE_GANHE_HEADERS,
    rows,
    'Template_Compre_e_Ganhe.xlsx',
  );
}

// ===================== LISTA SUGERIDA ORIGINAL (t4a) =====================

const LISTA_SUGERIDA_ORIGINAL_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Tipo de Lista': 'suggested list type \n1= Original \n2= Recompra\n*OBRIGATÓRIO',
  'Recompra disponível': 'A lista original pode gerar recompra (Yes / No) *OBRIGATÓRIO',
  'Nome da promo / cupom': 'Nome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom \n*OBRIGATÓRIO',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações. \n*OBRIGATÓRIO',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app. \n*OBRIGATÓRIO',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Número do card na promo': 'Valor numérico sequencial de acordo com a quantidade de cards na promo\n0 = card único,\n1 = card principal / pai,\n2 em diante = cards secundários / dependentes / filhos \n*OBRIGATÓRIO',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB \n*OBRIGATÓRIO',
  'Multi patrocinador': '0 = Não\n1 = Sim \n*OBRIGATÓRIO',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não \n*OBRIGATÓRIO',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss \n*OBRIGATÓRIO',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss \n*OBRIGATÓRIO',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver (*Não Obrigatório)',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção (*Não Obrigatório)',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção (*Não Obrigatório)',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto \n*OBRIGATÓRIO',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto  \n*OBRIGATÓRIO',
  'Nome da família': 'Sugestão de nome a ser utilizado na família \n*OBRIGATÓRIO',
  'Indústria da família': 'Apenas uma indústria por família \n*OBRIGATÓRIO',
  'OBS Família existente': 'Caso queira reaproveitar uma família existente, cite aqui o nome da família ou da promoção na qual ela foi utilizada',
  'SKUs da família': 'Preencher no campo as caixarias a serem incluídas',
  'Quantidade de EANs distintos para ativação da promo': 'Quantidade de itens diferentes para ativar a promoção *OBRIGATÓRIO',
  'Quantidade máxima para ativar a promo': 'Quantidade máxima de EANs para ativar a promo *OBRIGATÓRIO',
  'Quantidade de famílias para ativar a promo': 'Esse número deve ser:\n- maior ou igual ao total de famílias obrigatórias\n- menor ou igual ao total de famílias na promo \n*OBRIGATÓRIO',
  'Família obrigatória para ativação?': 'vazio = não\n1 = sim *OBRIGATÓRIO',
  'Posição da família dentro da lista': 'Boa prática: família principal / obrigatória / gatilho = 1\ne demais famílias = 2, 3... para que a promoção funcione\n*OBRIGATÓRIO',
  'Desconto da família': 'usar o campo de desconto',
  'Posição da lista dentro da seção': 'Boa prática para UL:\nLista G = 1\nLista M = 2\nLista P = 3\nLista PP = 4\nEm caso de empate será por ordem alfabética \n*OBRIGATÓRIO',
  'Seção na qual a lista deverá aparecer': 'Para UL: Cuidados pessoais, Cuidados com a Casa, Beleza e bem estar, Nutrição, etc.\nPara Multi: nome da indústria \n*OBRIGATÓRIO',
};

function buildListaSugeridaOriginalRow(values: FormValues): Row {
  return {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '4',
    'Tipo de Lista': raw(values['tipoLista']) || '1',
    'Recompra disponível': str(values['recompraDisponivel']),
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Número do card na promo': num(values['numeroCard']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Nome da família': str(values['nomeFamilia']),
    'Indústria da família': str(values['industriaFamilia']),
    'OBS Família existente': str(values['obsFamilia']),
    'SKUs da família': str(values['skusFamilia']),
    'Quantidade de EANs distintos para ativação da promo': num(values['qtdEANsDistintos']),
    'Quantidade máxima para ativar a promo': num(values['qtdMaximaAtivacao']),
    'Quantidade de famílias para ativar a promo': num(values['qtdFamiliasAtivacao']),
    'Família obrigatória para ativação?': raw(values['familiaObrigatoria']),
    'Posição da família dentro da lista': num(values['posicaoFamilia']),
    'Desconto da família': num(values['descontoFamilia']),
    'Posição da lista dentro da seção': num(values['posicaoLista']),
    'Seção na qual a lista deverá aparecer': str(values['secaoLista']),
  };
}

export function exportListaSugeridaOriginal(values: FormValues): void {
  const cols = Object.keys(LISTA_SUGERIDA_ORIGINAL_HEADERS);
  const rows = getParentAndChildValues('t4a', values).map(buildListaSugeridaOriginalRow);

  const emptyRow: Row = Object.fromEntries(cols.map((c) => [c, '']));
  const outputRows = rows.length > 1 ? rows : [rows[0], emptyRow];

  buildAndDownload(
    cols,
    LISTA_SUGERIDA_ORIGINAL_HEADERS,
    outputRows,
    'Template_Lista_Sugerida_Original.xlsx',
  );
}

// ===================== LISTA SUGERIDA RECOMPRA (t4b) =====================

const LISTA_SUGERIDA_RECOMPRA_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Tipo de Lista': 'suggested list type \n1= Original \n2= Recompra (Para esse template sempre 2) \n*OBRIGATÓRIO',
  'ID da lista sugerida de referência para recompra': 'Este campo deverá ficar no formato texto *OBRIGATÓRIO',
  'Nome da promo / cupom': 'Nome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom \n*OBRIGATÓRIO',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações. \n*OBRIGATÓRIO',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app. \n*OBRIGATÓRIO',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Número do card na promo': 'Valor numérico sequencial de acordo com a quantidade de cards na promo\n0 = card único,\n1 = card principal / pai,\n2 em diante = cards secundários / dependentes / filhos \n*OBRIGATÓRIO',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB \n*OBRIGATÓRIO',
  'Multi patrocinador': '0 = Não\n1 = Sim \n*OBRIGATÓRIO',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não \n*OBRIGATÓRIO',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss \n*OBRIGATÓRIO',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss \n*OBRIGATÓRIO',
  'Budget atrelado?': '1 = Sim\n2 = Não \n*OBRIGATÓRIO',
  'Budget (R$)': 'Apenas o valor numérico, se houver (*Não Obrigatório)',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços. \n*OBRIGATÓRIO',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui o nome da segmentação da lista original de referência. Sugerimos usar o ID da lista original. Esses CNPJs terão direito à promoção \n*OBRIGATÓRIO',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção ',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto \n*OBRIGATÓRIO',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto \n*OBRIGATÓRIO',
  'Nome da família': 'Sugestão de nome a ser utilizado na família \n*OBRIGATÓRIO',
  'Indústria da família': 'Apenas uma indústria por família \n*OBRIGATÓRIO',
  'OBS Família existente': 'Caso queira reaproveitar uma família existente, cite aqui o nome da família ou da promoção na qual ela foi utilizada',
  'SKUs da família': 'Preencher no campo as caixarias a serem incluídas',
  'Quantidade de EANs distintos para ativação da promo': 'Quantidade de itens diferentes para ativar a promoção *NÃO OBRIGATÓRIO',
  'Quantidade máxima para ativar a promo': 'Quantidade máxima de EANs para ativar a promo *NÃO OBRIGATÓRIO',
  'Quantidade de famílias para ativar a promo': 'Esse número deve ser:\n- maior ou igual ao total de famílias obrigatórias\n- menor ou igual ao total de famílias na promo',
  'Família obrigatória para ativação?': 'vazio = não\n1 = sim *NÃO OBRIGATÓRIO',
  'Posição da família dentro da lista': 'Boa prática: família principal / obrigatória / gatilho = 1\ne demais famílias = 2, 3... para que a promoção funcione \n*OBRIGATÓRIO',
  'Desconto da família': 'usar o campo de desconto',
  'Posição da lista dentro da seção': 'Boa prática para UL:\nLista G = 1\nLista M = 2\nLista P = 3\nLista PP = 4\nEm caso de empate será por ordem alfabética \n*OBRIGATÓRIO',
  'Seção na qual a lista deverá aparecer': 'Para UL: Cuidados pessoais, Cuidados com a Casa, Beleza e bem estar, Nutrição, etc.\nPara Multi: nome da indústria \n*OBRIGATÓRIO',
};

function buildListaSugeridaRecompraRow(values: FormValues): Row {
  return {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '4',
    'Tipo de Lista': '2',
    'ID da lista sugerida de referência para recompra': str(values['idListaOriginal']),
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Número do card na promo': num(values['numeroCard']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Nome da família': str(values['nomeFamilia']),
    'Indústria da família': str(values['industriaFamilia']),
    'OBS Família existente': str(values['obsFamilia']),
    'SKUs da família': str(values['skusFamilia']),
    'Quantidade de EANs distintos para ativação da promo': num(values['qtdEANsDistintos']),
    'Quantidade máxima para ativar a promo': num(values['qtdMaximaAtivacao']),
    'Quantidade de famílias para ativar a promo': num(values['qtdFamiliasAtivacao']),
    'Família obrigatória para ativação?': raw(values['familiaObrigatoria']),
    'Posição da família dentro da lista': num(values['posicaoFamilia']),
    'Desconto da família': num(values['descontoFamilia']),
    'Posição da lista dentro da seção': num(values['posicaoLista']),
    'Seção na qual a lista deverá aparecer': str(values['secaoLista']),
  };
}

export function exportListaSugeridaRecompra(values: FormValues): void {
  const cols = Object.keys(LISTA_SUGERIDA_RECOMPRA_HEADERS);
  const rows = getParentAndChildValues('t4b', values).map(buildListaSugeridaRecompraRow);

  buildAndDownload(
    cols,
    LISTA_SUGERIDA_RECOMPRA_HEADERS,
    rows,
    'Template_Lista_Sugerida_Recompra.xlsx',
  );
}

// ===================== CUPOM SIMPLES (t5) =====================

const CUPOM_SIMPLES_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Nome da promo / cupom': 'DEVE SER O MESMO PARA TODOS OS CARDS DE UMA MESMA PROMOÇÃO.\nNome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Tipo de desconto promocional': '1 = By_percent: valor percentual de desconto do item a ser fornecida\n2 = Cart_fixed: valor bruto (R$) de desconto no valor total do carrinho a ser fornecido\n3 = By_fixed: valor bruto (R$) de desconto do item a ser fornecido',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual ou monetário, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%" e "R$"',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Critério de desconto': '1 = Quantidade min/máx\n2 = Valor min/máx\nPreencha os campos a seguir de acordo com a sua escolha',
  'Quantidade mínima': 'Quantidade mínima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Quantidade máxima': 'Quantidade máxima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Código do cupom': 'Máx. 60 caracteres\nSem espaços\nCaracteres especiais permitidos: hífen (-) e underline (_)',
  'Quantidade de cupons disponíveis': 'Se ilimitado, preencher como 99999',
  'Quantidade de usos por cliente': 'Se ilimitado, preencher como 99999',
  'Tipo de item a ser INCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem INCLUÍDOS na promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'EANs obrigatórios': 'Preencher os EANs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKUs obrigatórios': 'Preencher os SKUs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKU é combo?': '0 = Não\n1 = Sim\nPreencher caso Itens a serem incluídos na promoção = 2 (SKUs)',
  'Indústria da família': 'Apenas uma indústria por família',
};

export function exportCupomSimples(values: FormValues): void {
  const cols = Object.keys(CUPOM_SIMPLES_HEADERS);

  const dataRow: Row = {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '5',
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Tipo de desconto promocional': raw(values['tipoDesconto']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Critério de desconto': raw(values['criterioDesconto']),
    'Quantidade mínima': num(values['qtdMinima']),
    'Quantidade máxima': num(values['qtdMaxima']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Código do cupom': str(values['codigoCupom']),
    'Quantidade de cupons disponíveis': num(values['qtdCupons']),
    'Quantidade de usos por cliente': num(values['usosPorCliente']),
    'Tipo de item a ser INCLUÍDO na promoção': raw(values['tipoItemIncluido']),
    'Itens a serem INCLUÍDOS na promoção': str(values['itensIncluidos']),
    'EANs obrigatórios': str(values['eansObrigatorios']),
    'SKUs obrigatórios': str(values['skusObrigatorios']),
    'SKU é combo?': raw(values['skuCombo']),
    'Indústria da família': str(values['industriaFamilia']),
  };

  buildAndDownload(cols, CUPOM_SIMPLES_HEADERS, [dataRow], 'Template_Cupom_Simples.xlsx');
}

// ===================== CUPOM PROGRESSIVO (t6) =====================

const CUPOM_PROGRESSIVO_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cupom + itens obrigatórios\n8 = Cashback',
  'Nome da promo / cupom': 'Nome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Número do card na promo': 'Valor numérico sequencial de acordo com a quantidade de cards na promo\n1 = card principal / pai,\n2 em diante = cards secundários / dependentes / filhos',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%"',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Critério de desconto': '1 = Quantidade min/máx\n2 = Valor min/máx\nPreencha os campos a seguir de acordo com a sua escolha',
  'Quantidade mínima': 'Quantidade mínima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Quantidade máxima': 'Quantidade máxima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Código do cupom': 'Máx. 60 caracteres\nSem espaços\nCaracteres especiais permitidos: hífen (-) e underline (_)O código do cupom deve ser único e nunca ter sido utilizado anteriormente',
  'Quantidade de cupons disponíveis': 'Se ilimitado, preencher como 99999',
  'Quantidade de usos por cliente': 'Se ilimitado, preencher como 99999',
  'Tipo de item a ser INCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem INCLUÍDOS na promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'Tipo de item a ser EXCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem EXCLUÍDOS da promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'EANs obrigatórios': 'Preencher os EANs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKUs obrigatórios': 'Preencher os SKUs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKU é combo?': '0 = Não\n1 = Sim\nPreencher caso Itens a serem incluídos na promoção = 2',
};

export function exportCupomProgressivo(values: FormValues): void {
  const cols = Object.keys(CUPOM_PROGRESSIVO_HEADERS);

  const dataRow: Row = {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '6',
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Número do card na promo': num(values['numeroCard']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Critério de desconto': raw(values['criterioDesconto']),
    'Quantidade mínima': num(values['qtdMinima']),
    'Quantidade máxima': num(values['qtdMaxima']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Código do cupom': str(values['codigoCupom']),
    'Quantidade de cupons disponíveis': num(values['qtdCupons']),
    'Quantidade de usos por cliente': num(values['usosPorCliente']),
    'Tipo de item a ser INCLUÍDO na promoção': raw(values['tipoItemIncluido']),
    'Itens a serem INCLUÍDOS na promoção': str(values['itensIncluidos']),
    'Tipo de item a ser EXCLUÍDO na promoção': raw(values['tipoItemExcluido']),
    'Itens a serem EXCLUÍDOS da promoção': str(values['itensExcluidos']),
    'EANs obrigatórios': str(values['eansObrigatorios']),
    'SKUs obrigatórios': str(values['skusObrigatorios']),
    'SKU é combo?': raw(values['skuCombo']),
  };

  buildAndDownload(cols, CUPOM_PROGRESSIVO_HEADERS, [dataRow], 'Template_Cupom_Progressivo.xlsx');
}

// ===================== CASHBACK (t7) =====================

const CASHBACK_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Nome da promo / cupom': 'DEVE SER O MESMO PARA TODOS OS CARDS DE UMA MESMA PROMOÇÃO.\nNome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual ou monetário, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%" e "R$"',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Critério de desconto': '1 = Quantidade min/máx\n2 = Valor min/máx\nPreencha os campos a seguir de acordo com a sua escolha',
  'Quantidade mínima': 'Quantidade mínima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Quantidade máxima': 'Quantidade máxima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Código do cupom': 'Máx. 60 caracteres\nSem espaços\nCaracteres especiais permitidos: hífen (-) e underline (_)',
  'Quantidade de cupons disponíveis': 'Se ilimitado, preencher como 99999',
  'Quantidade de usos por cliente': 'Se ilimitado, preencher como 99999',
  'Tipo de item a ser INCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem INCLUÍDOS na promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'Tipo de item a ser EXCLUÍDO na promoção': '1 = EANs\n2 = SKUs (incluir tb os unitários)\n3 = Marcas\n4 = Categorias\n5 = Subcategorias\n6 = Departamentos\n7 = Indústrias\n8 = Famílias',
  'Itens a serem EXCLUÍDOS da promoção': 'Preencher de acordo com o padrão selecionado no campo anterior.\nSe for SKUs, incluir também o SKU unitário',
  'EANs obrigatórios': 'Preencher os EANs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKUs obrigatórios': 'Preencher os SKUs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKU é combo?': '0 = Não\n1 = Sim\nPreencher caso Itens a serem incluídos na promoção = 2 (SKUs)',
};

export function exportCashback(values: FormValues): void {
  const cols = Object.keys(CASHBACK_HEADERS);

  const dataRow: Row = {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': '7',
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Critério de desconto': raw(values['criterioDesconto']),
    'Quantidade mínima': num(values['qtdMinima']),
    'Quantidade máxima': num(values['qtdMaxima']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Código do cupom': str(values['codigoCupom']),
    'Quantidade de cupons disponíveis': num(values['qtdCupons']),
    'Quantidade de usos por cliente': num(values['usosPorCliente']),
    'Tipo de item a ser INCLUÍDO na promoção': raw(values['tipoItemIncluido']),
    'Itens a serem INCLUÍDOS na promoção': str(values['itensIncluidos']),
    'Tipo de item a ser EXCLUÍDO na promoção': raw(values['tipoItemExcluido']),
    'Itens a serem EXCLUÍDOS da promoção': str(values['itensExcluidos']),
    'EANs obrigatórios': str(values['eansObrigatorios']),
    'SKUs obrigatórios': str(values['skusObrigatorios']),
    'SKU é combo?': raw(values['skuCombo']),
  };

  buildAndDownload(cols, CASHBACK_HEADERS, [dataRow], 'Template_Cashback.xlsx');
}

// ===================== REBAIXA / CUPOM GERAL (t8) =====================

const REBAIXA_CUPOM_GERAL_HEADERS: Row = {
  'Tipo de solicitação': '1 = Legado (existe na IFC)\n0 / vazio = promoção nova',
  'ID Infracommerce': 'Preenchimento obrigatório para promoções existentes na IFC. Para as promoções que não possuem correspondência na IFC, escrever "n/a"',
  'Tipo de mecânica promocional': '1 = Rebaixa Simples sem cupom\n2 = Rebaixa progressiva sem cupom\n3 = Compre e Ganhe / A+B\n4 = Lista sugerida\n5 = Cupom Simples\n6 = Cupom Progressivo\n7 = Cashback',
  'Nome da promo / cupom': 'DEVE SER O MESMO PARA TODOS OS CARDS DE UMA MESMA PROMOÇÃO.\nNome interno da promo - pode ser igual ou não ao que aparecerá na página. Deve ser o mesmo para todos os cards de uma mesma promoção.\nSe for cupom = nome do cupom',
  'Nome de exibição da promoção': 'Nome que será exibido no site, app e nas comunicações.',
  'Descrição da promoção': 'Descreva detalhadamente a promo em até 250 caracteres: valor do desconto (R$ ou %), duração da promoção e regras.\nEssa descrição será exibida no site e app.',
  'Descrição interna de campanha': 'Descrição para controle interno. Essa descrição não será exibida no site e app.',
  'Número do card na promo': 'Valor numérico sequencial de acordo com a quantidade de cards na promo\n0 = card único\n1 = card principal / pai,\n2 em diante = cards secundários / dependentes / filhos',
  'Tipo de desconto promocional': '1 = By_percent: valor percentual de desconto do item a ser fornecida\n2 = Cart_fixed: valor bruto (R$) de desconto no valor total do carrinho a ser fornecido\n3 = By_fixed: valor bruto (R$) de desconto do item a ser fornecido',
  'Valor do desconto (R$ ou %)': 'O desconto será em valor percentual ou monetário, de acordo com a escolha do campo anterior. Não se deve incluir os caracteres de "%" e "R$"',
  'Indústria': '1 = Unilever\n2 = Multi\n3 = E-UB',
  'Multi patrocinador': '0 = Não\n1 = Sim',
  'Patrocinador(es) e contribuição': 'Liste o(s) patrocinador(es) e a contribuição (percentual ou valor) que cada um irá oferecer',
  'Promoção cumulativa?': '1 = Sim\n2 = Não',
  'Início da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Fim da vigência': 'Formato: \ndd/mm/yyyy hh:mm:ss',
  'Budget atrelado?': '1 = Sim\n2 = Não',
  'Budget (R$)': 'Apenas o valor numérico, se houver',
  'Sellers incluídos': 'Escrever "All" para incluir todos ou os nomes dos sellers específicos a serem incluídos na promo.\nSeparar por vírgula e sem espaços.',
  'Sellers exceção': 'Caso comente "All" no campo anterior tenha algum seller que não será contemplado pela promoção, favor listar aqui.\nSeparar por vírgula e sem espaços.',
  'CNPJs incluídos (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs terão direito à promoção',
  'CNPJs exceção (varejistas)': 'Inclua aqui os CNPJs caso queira criar uma segmentação na qual apenas esses CNPJs ficarão de fora da promoção',
  'Canais de veiculação de promoção': '1 = Site\n2 = App\n3 = Whatsapp\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Tipo de pedido': '1 = Autônomo\n2 = Venda assistida\nCaso queira incluir mais de um, separe-os por vírgula e sem espaço. Este campo deverá ficar no formato texto',
  'Critério de desconto': '1 = Quantidade min/máx\n2 = Valor min/máx\nPreencha os campos a seguir de acordo com a sua escolha',
  'Quantidade mínima': 'Quantidade mínima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Quantidade máxima': 'Quantidade máxima para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 1',
  'Valor mínimo (R$)': 'Valor mínimo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Valor máximo (R$)': 'Valor máximo para ativar a promoção (em unidades)\nPreencher apenas se Critério de desconto = 2',
  'Cupom': '1 = Sem cupom\n2 = Com cupom',
  'Código do cupom': 'Máx. 60 caracteres\nSem espaços\nCaracteres especiais permitidos: hífen (-) e underline (_)\nPreencher apenas se Cupom = 2',
  'Quantidade de cupons disponíveis': 'Se ilimitado, preencher como 99999\nPreencher apenas se Cupom =2',
  'Quantidade de usos por cliente': 'Se ilimitado, preencher como 99999\nPreencher apenas se Cupom =2',
  'EANs inclusão': '',
  'EANs exclusão': '',
  'SKUs inclusão': '',
  'SKUs exclusão': '',
  'Marcas inclusão': '',
  'Marcas exclusão': '',
  'Categorias inclusão': '',
  'Categorias exclusão': '',
  'Subcategorias inclusão': '',
  'Subcategorias exclusão': '',
  'Departamentos inclusão': '',
  'Departamentos exclusão': '',
  'Indústrias inclusão': '',
  'Indústrias exclusão': '',
  'Famílias inclusão': '',
  'Famílias exclusão': '',
  'EANs obrigatórios': 'Preencher os EANs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKUs obrigatórios': 'Preencher os SKUs obrigatórios para ativar a promoção, separados por vírgula e sem espaço',
  'SKU é combo?': '0 = Não\n1 = Sim\nPreencher caso Itens a serem incluídos na promoção = 2',
};

export function exportRebaixaCupomGeral(values: FormValues): void {
  const cols = Object.keys(REBAIXA_CUPOM_GERAL_HEADERS);

  const dataRow: Row = {
    'Tipo de solicitação': raw(values['tipoSolicitacao']),
    'ID Infracommerce': str(values['idIFC']),
    'Tipo de mecânica promocional': raw(values['tipoMecanica']) || (raw(values['cupom']) === '2' ? '5' : '1'),
    'Nome da promo / cupom': str(values['nomePromo']),
    'Nome de exibição da promoção': str(values['nomeExibicao']),
    'Descrição da promoção': str(values['descricao']),
    'Descrição interna de campanha': str(values['descricaoInterna']),
    'Número do card na promo': num(values['numeroCard']),
    'Tipo de desconto promocional': raw(values['tipoDesconto']),
    'Valor do desconto (R$ ou %)': num(values['valorDesconto']),
    'Indústria': raw(values['industria']),
    'Multi patrocinador': raw(values['multiPatrocinador']),
    'Patrocinador(es) e contribuição': str(values['patrocinadores']),
    'Promoção cumulativa?': raw(values['cumulativa']),
    'Início da vigência': fmtDate(values['inicioVigencia']),
    'Fim da vigência': fmtDate(values['fimVigencia']),
    'Budget atrelado?': raw(values['budgetAtrelado']),
    'Budget (R$)': num(values['budgetValor']),
    'Sellers incluídos': fmtSellers(values['sellersIncluidos']),
    'Sellers exceção': fmtSellers(values['sellersExcecao']),
    'CNPJs incluídos (varejistas)': str(values['cnpjsIncluidos']),
    'CNPJs exceção (varejistas)': str(values['cnpjsExcecao']),
    'Canais de veiculação de promoção': rawMulti(values['canais']),
    'Tipo de pedido': rawMulti(values['tipoPedido']),
    'Critério de desconto': raw(values['criterioDesconto']),
    'Quantidade mínima': num(values['qtdMinima']),
    'Quantidade máxima': num(values['qtdMaxima']),
    'Valor mínimo (R$)': num(values['valorMinimo']),
    'Valor máximo (R$)': num(values['valorMaximo']),
    'Cupom': raw(values['cupom']),
    'Código do cupom': str(values['codigoCupom']),
    'Quantidade de cupons disponíveis': num(values['qtdCupons']),
    'Quantidade de usos por cliente': num(values['usosPorCliente']),
    'EANs inclusão': str(values['eansInclusao']),
    'EANs exclusão': str(values['eansExclusao']),
    'SKUs inclusão': str(values['skusInclusao']),
    'SKUs exclusão': str(values['skusExclusao']),
    'Marcas inclusão': str(values['marcasInclusao']),
    'Marcas exclusão': str(values['marcasExclusao']),
    'Categorias inclusão': str(values['categoriasInclusao']),
    'Categorias exclusão': str(values['categoriasExclusao']),
    'Subcategorias inclusão': str(values['subcategoriasInclusao']),
    'Subcategorias exclusão': str(values['subcategoriasExclusao']),
    'Departamentos inclusão': str(values['departamentosInclusao']),
    'Departamentos exclusão': str(values['departamentosExclusao']),
    'Indústrias inclusão': str(values['industriasInclusao']),
    'Indústrias exclusão': str(values['industriasExclusao']),
    'Famílias inclusão': str(values['familiasInclusao']),
    'Famílias exclusão': str(values['familiasExclusao']),
    'EANs obrigatórios': str(values['eansObrigatorios']),
    'SKUs obrigatórios': str(values['skusObrigatorios']),
    'SKU é combo?': raw(values['skuCombo']),
  };

  buildAndDownload(cols, REBAIXA_CUPOM_GERAL_HEADERS, [dataRow], 'Template_Rebaixa_Cupom_Geral.xlsx');
}

const TEMPLATE_EXPORTERS_BY_ID: Record<string, XlsxExporter> = {
  t1: exportRebaixaSimples,
  t2: exportRebaixaProgressiva,
  t3: exportCompreEGanhe,
  t4a: exportListaSugeridaOriginal,
  t4b: exportListaSugeridaRecompra,
  t5: exportCupomSimples,
  t6: exportCupomProgressivo,
  t7: exportCashback,
  t8: exportRebaixaCupomGeral,
};

function getRecordValues(record: FormValues | { values: FormValues }): FormValues {
  if (
    record &&
    typeof record === 'object' &&
    'values' in record &&
    record.values &&
    typeof record.values === 'object'
  ) {
    return record.values as FormValues;
  }

  return record as FormValues;
}

function collectExportPayload(exporter: XlsxExporter, values: FormValues): XlsxExportPayload | null {
  const previousCollector = activeXlsxExportCollector;
  const collector: { payload?: XlsxExportPayload } = {};
  activeXlsxExportCollector = collector;

  try {
    exporter(values);
  } finally {
    activeXlsxExportCollector = previousCollector;
  }

  return collector.payload ?? null;
}

function makeBatchFileName(fileName: string): string {
  return fileName.replace(/\.xlsx$/i, '_Lote.xlsx');
}

export function exportBriefingsByTemplate(
  templateId: string,
  records: Array<FormValues | { values: FormValues }>,
): void {
  const exporter = TEMPLATE_EXPORTERS_BY_ID[templateId];
  if (!exporter || records.length === 0) return;

  const payloads = records
    .map((record) => collectExportPayload(exporter, getRecordValues(record)))
    .filter((payload): payload is XlsxExportPayload => Boolean(payload));

  const firstPayload = payloads[0];
  if (!firstPayload) return;

  const rows = payloads
    .flatMap((payload) => (
      CHILD_EXPORT_TEMPLATE_IDS.has(templateId)
        ? payload.rows
        : [payload.rows[0]]
    ))
    .filter((row): row is Row => Boolean(row))
    .filter(isMeaningfulRow);

  if (rows.length === 0) return;

  buildAndDownload(
    firstPayload.cols,
    firstPayload.headers,
    rows,
    makeBatchFileName(firstPayload.fileName),
  );
}

/*function buildAndDownload(
  cols: string[],
  headers: Row,
  dataRows: Row[],
  filename: string,
): void {
  const wb = XLSX.utils.book_new();

  // Row 1: header comments, Row 2: column names, Row 3+: data
  const aoaData: (string | number)[][] = [
    cols.map((c) => String(headers[c] ?? '')),
    cols,
    ...dataRows.map((row) => cols.map((c) => row[c] ?? '')),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoaData);

  // Style header comment row (row 1) with wrap text
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (cell) {
      cell.s = { alignment: { wrapText: true, vertical: 'top' } };
    }
  }

  // Set column widths
  ws['!cols'] = cols.map(() => ({ wch: 30 }));

  XLSX.utils.book_append_sheet(wb, ws, 'Briefing');
  XLSX.writeFile(wb, filename);
}*/

function buildAndDownload(
  cols: string[],
  headers: Row,
  rows: Row[],
  fileName: string,
): void {
  if (activeXlsxExportCollector) {
    activeXlsxExportCollector.payload = { cols, headers, rows, fileName };
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: cols,
    skipHeader: false,
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

  XLSX.writeFile(workbook, fileName);
}
